// @ts-check

/**
 * @file Provides sign-in and sign-out helpers for Velocious auth.
 */

import {clearCurrentUser, refreshCurrentUser} from "../auth/use-current-user"
import {User} from "../frontend-models/user"
import {clearSessionToken, setSessionToken} from "./session-option"
import {assertReplicaMayBeCleared, beginReplicaBootstrap, clearReplica, sealReplica} from "../database/replica-lifecycle"
import {projectSyncLifecycle} from "../sync/project-sync-lifecycle"

/**
 * Sign-in / sign-out helpers backed by the Velocious `User.signIn` and
 * `User.signOut` collection commands. The opaque session token returned by
 * the backend is persisted in the local Velocious `Option` table via
 * `session-option`, and attached to every subsequent Velocious request by
 * the transport's `requestHeaders` hook (see `velocious/initialize.js`).
 */

/**
 * Signs in with credentials and hydrates current-user state.
 * @param {string} email Email address entered by the user.
 * @param {string} password Password entered by the user.
 * @param {{rememberMe?: boolean}} [options] When `rememberMe` is true the backend stamps a longer session lifetime.
 * @returns {Promise<void>}
 * @rejects {Error} Rejects when the backend does not return a session token.
 */
export async function signIn(email, password, {rememberMe = false} = {}) {
  const response = /** @type {{sessionToken?: string, sessionExpiresAt?: string, user?: {email: string | null, id: string | number}}} */ (
    await User.signIn({email, password, rememberMe})
  )

  if (!response || typeof response.sessionToken !== "string" || response.sessionToken.length === 0) {
    throw new Error("Sign in succeeded but no session token was returned")
  }

  const signedInUser = response.user
  if (!signedInUser?.id) throw new Error("Sign in succeeded but no user identity was returned")

  await beginReplicaBootstrap({
    admin: false, email: signedInUser.email ?? email, first_name: null, id: signedInUser.id,
    last_name: null, locale: null, name: signedInUser.email ?? email
  })
  await setSessionToken({
    expiresAt: response.sessionExpiresAt || null,
    token: response.sessionToken,
    userId: signedInUser.id
  })

  // Pull the full profile (abilities, theme, locale, name fields) into the
  // shared current-user state so BoardLayout and other consumers reading
  // through the WithCurrentUser context see the signed-in user without
  // each needing its own currentProfile round trip.
  const profile = await refreshCurrentUser()
  if (!profile || String(profile.id) !== String(signedInUser.id)) {
    await sealReplica()
    throw new Error("Signed-in profile did not match the staged replica owner")
  }
}

/**
 * Signs out locally and remotely.
 * @returns {Promise<void>}
 */
export async function signOut() {
  await assertReplicaMayBeCleared()
  await sealReplica()
  await projectSyncLifecycle.stop()
  try {
    await User.signOut()
  } finally {
    // Always clear local state even if the backend destroy failed —
    // otherwise a transient error leaves the SPA thinking it is still
    // signed in and the user cannot recover without clearing storage.
    try {
      await clearReplica()
    } finally {
      await clearSessionToken()
      clearCurrentUser()
    }
  }
}
